import React, { useMemo, useState } from 'react'
import { Loader2, Lightbulb, Compass, AlertTriangle, Plus, X, History as HistoryIcon, Trash2 } from 'lucide-react'
import { useProjects } from '../hooks/useRag'
import {
  useMindmap,
  useAnalyzeTopics,
  useTopicHistory,
  useDeleteTopicAnalysis,
} from '../hooks/useResearch'

// The five gap-analysis categories (keys match the engine's GAP_ANALYSIS_PROMPT).
const GAP_CATS = [
  ['missing_subtopics', 'Missing subtopics'],
  ['weak_coverage', 'Weak coverage'],
  ['missing_connections', 'Missing connections'],
  ['open_questions', 'Open questions'],
  ['research_suggestions', 'Research suggestions'],
]

// Items often arrive as JSON-like strings (e.g. '{"topic":"…","why_important":"…"}')
// rather than parsed objects — normalize both into a usable value.
function parseItem(it) {
  if (it && typeof it === 'object') return it
  if (typeof it === 'string') {
    const s = it.trim()
    if (s.startsWith('{') && s.endsWith('}')) {
      try { return JSON.parse(s) } catch { /* fall through to raw string */ }
    }
    return s
  }
  return it
}

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  } catch { return iso }
}

function Badge({ children, tone = 'default' }) {
  const tones = {
    default: 'bg-parchment-200 text-ink-soft dark:bg-midnight-700 dark:text-mist/60',
    gold: 'bg-gold/15 text-gold-deep dark:text-gold',
    spark: 'bg-spark/10 text-spark-600 dark:text-spark',
  }
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${tones[tone]}`}>{children}</span>
}

// One gap-analysis item, rendered per category.
function GapItem({ cat, item }) {
  const v = parseItem(item)
  if (v == null) return null
  if (typeof v === 'string') return <li className="text-sm text-ink/90 dark:text-mist/90">{v}</li>

  if (cat === 'missing_subtopics') {
    return (
      <li className="text-sm">
        <span className="font-medium text-ink dark:text-mist">{v.topic}</span>
        {v.why_important && <span className="text-ink-soft dark:text-mist/60"> — {v.why_important}</span>}
      </li>
    )
  }
  if (cat === 'weak_coverage') {
    return (
      <li className="text-sm">
        <span className="font-medium text-ink dark:text-mist">{v.topic}</span>
        <div className="text-xs text-ink-soft dark:text-mist/60">
          {v.current_state && <>now: {v.current_state}</>}
          {v.needed && <> · needs: {v.needed}</>}
        </div>
      </li>
    )
  }
  if (cat === 'missing_connections') {
    return (
      <li className="text-sm">
        <span className="font-medium text-ink dark:text-mist">{v.from} → {v.to}</span>
        {v.suggested_relationship && <div className="text-xs text-ink-soft dark:text-mist/60">{v.suggested_relationship}</div>}
      </li>
    )
  }
  if (cat === 'open_questions') {
    return (
      <li className="text-sm text-ink/90 dark:text-mist/90">
        {v.question || v.text}
        {v.context && <div className="text-xs text-ink-soft dark:text-mist/60">{v.context}</div>}
      </li>
    )
  }
  if (cat === 'research_suggestions') {
    return (
      <li className="text-sm">
        <span className="text-ink/90 dark:text-mist/90">{v.suggestion}</span>{' '}
        {v.priority && <Badge tone="gold">{v.priority}</Badge>} {v.type && <Badge>{v.type}</Badge>}
      </li>
    )
  }
  return <li className="text-sm text-ink-soft">{JSON.stringify(v)}</li>
}

// Gap analysis with category tabs.
function GapAnalysis({ gap }) {
  const cats = useMemo(
    () => GAP_CATS.map(([key, label]) => ({ key, label, items: Array.isArray(gap?.[key]) ? gap[key] : [] })),
    [gap]
  )
  const [active, setActive] = useState(() => cats.find((c) => c.items.length)?.key || cats[0].key)

  if (gap?.error) return <p className="text-sm text-red-600">{gap.error}</p>
  const current = cats.find((c) => c.key === active) || cats[0]

  return (
    <div>
      <div className="flex flex-wrap gap-1 border-b border-parchment-border dark:border-midnight-600">
        {cats.map((c) => (
          <button
            key={c.key}
            onClick={() => setActive(c.key)}
            className={`px-2.5 py-1.5 text-xs rounded-t-md -mb-px border-b-2 transition ${
              active === c.key
                ? 'border-gold text-gold-deep dark:text-gold'
                : 'border-transparent text-ink-soft hover:text-ink dark:text-mist/50 dark:hover:text-mist'
            }`}
          >
            {c.label} <span className="opacity-60">{c.items.length}</span>
          </button>
        ))}
      </div>
      <ul className="mt-3 space-y-2">
        {current.items.length ? (
          current.items.map((it, i) => <GapItem key={i} cat={current.key} item={it} />)
        ) : (
          <li className="text-sm text-ink-soft dark:text-mist/50 italic">No items in this category.</li>
        )}
      </ul>
    </div>
  )
}

function ResearchQuestions({ rq }) {
  if (rq?.error) return <p className="text-sm text-red-600">{rq.error}</p>
  const list = Array.isArray(rq?.questions) ? rq.questions : Array.isArray(rq) ? rq : []
  if (!list.length) return <p className="text-sm text-ink-soft dark:text-mist/50 italic">No questions.</p>
  return (
    <ol className="space-y-2.5 list-decimal pl-5">
      {list.map((q, i) => {
        const v = parseItem(q)
        const text = typeof v === 'string' ? v : v.question || v.text
        return (
          <li key={i} className="text-sm text-ink/90 dark:text-mist/90">
            <span>{text}</span> {v?.type && <Badge tone="spark">{v.type}</Badge>}
            {Array.isArray(v?.search_terms) && v.search_terms.length > 0 && (
              <div className="text-xs text-ink-soft dark:text-mist/50">search: {v.search_terms.join(' · ')}</div>
            )}
          </li>
        )
      })}
    </ol>
  )
}

function TopicResult({ topic, entry, onPickConcept, onDiscover }) {
  return (
    <article className="rounded-xl border border-parchment-border bg-white/70 p-5 dark:border-midnight-600 dark:bg-midnight-800/60">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-display text-lg text-ink dark:text-mist">{topic}</h3>
        <div className="flex items-center gap-3 shrink-0">
          {onDiscover && (
            <button
              onClick={() => onDiscover(topic)}
              className="text-xs text-gold-deep dark:text-gold hover:underline"
              title="Find channels & videos covering this topic (gap-driven acquisition)"
            >
              Find sources →
            </button>
          )}
          <button onClick={() => onPickConcept?.(topic)} className="text-xs text-gold-deep dark:text-gold hover:underline">
            Ask about this →
          </button>
        </div>
      </div>
      <div className="mt-4 grid lg:grid-cols-2 gap-6">
        {entry.gap_analysis && (
          <div>
            <div className="flex items-center gap-1.5 text-sm font-medium text-ink dark:text-mist mb-2">
              <Compass className="w-4 h-4 text-gold-deep dark:text-gold" /> Gap analysis
            </div>
            <GapAnalysis gap={entry.gap_analysis} />
          </div>
        )}
        {entry.research_questions && (
          <div>
            <div className="flex items-center gap-1.5 text-sm font-medium text-ink dark:text-mist mb-2">
              <Lightbulb className="w-4 h-4 text-gold-deep dark:text-gold" /> Research questions
            </div>
            <ResearchQuestions rq={entry.research_questions} />
          </div>
        )}
      </div>
    </article>
  )
}

export default function TopicsPanel({ onPickConcept, onDiscover }) {
  const projectsQuery = useProjects()
  const [project, setProject] = useState('')
  const [selected, setSelected] = useState([])
  const analyze = useAnalyzeTopics()
  const [results, setResults] = useState(null)
  const [viewingId, setViewingId] = useState(null)

  React.useEffect(() => {
    if (!project && projectsQuery.data?.current_project) setProject(projectsQuery.data.current_project)
  }, [project, projectsQuery.data])

  const map = useMindmap(project, true)
  const history = useTopicHistory(project)
  const delAnalysis = useDeleteTopicAnalysis()
  const projects = projectsQuery.data?.projects ?? []

  const concepts = useMemo(() => {
    const nodes = Object.values(map.data?.data?.nodes || {})
    return nodes
      .filter((n) => n.node_type === 'concept')
      .sort((a, b) => (b.mention_count || 0) - (a.mention_count || 0))
      .map((n) => n.label)
      .slice(0, 40)
  }, [map.data])

  const clusters = Object.values(map.data?.data?.clusters || {})
  const themes = clusters.filter((c) => c.cluster_level === 1)

  const toggle = (label) => setSelected((s) => (s.includes(label) ? s.filter((x) => x !== label) : [...s, label]))

  const run = () => {
    if (!project || selected.length === 0) return
    setResults(null)
    setViewingId(null)
    analyze.mutate(
      { project, topics: selected, kinds: ['gap', 'questions'] },
      { onSuccess: (r) => { setResults(r.results); setViewingId(r.id) } }
    )
  }

  const hasMap = map.data?.exists && map.data.total_nodes > 0
  const analyses = history.data?.analyses ?? []

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={project}
          onChange={(e) => { setProject(e.target.value); setSelected([]); setResults(null); setViewingId(null) }}
          className="h-9 rounded-lg px-3 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist"
        >
          <option value="">Select a project…</option>
          {projects.map((p) => <option key={p.name} value={p.name}>{p.name}</option>)}
        </select>
        <button
          onClick={run}
          disabled={!project || selected.length === 0 || analyze.isPending}
          className="inline-flex items-center gap-2 px-4 h-9 rounded-lg text-sm font-medium bg-gradient-to-r from-gold to-spark text-midnight-900 hover:opacity-90 disabled:opacity-40 transition"
        >
          {analyze.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass className="w-4 h-4" />}
          Analyze {selected.length > 0 ? `(${selected.length})` : 'topics'}
        </button>
      </div>

      {!hasMap && project && !map.isLoading && (
        <div className="rounded-xl border border-dashed border-parchment-border p-6 text-center text-ink-soft dark:border-midnight-600 dark:text-mist/50">
          Build the knowledge map for <span className="text-gold-deep dark:text-gold">{project}</span> first (Knowledge Map tab) — topic analysis runs on its concepts.
        </div>
      )}

      {themes.length > 0 && (
        <div>
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-2">Themes</h3>
          <div className="flex flex-wrap gap-2">
            {themes.map((t) => (
              <span key={t.id} className="px-2.5 py-1 rounded-full text-xs border border-gold/40 text-ink dark:text-mist" title={t.description || ''}>{t.label}</span>
            ))}
          </div>
        </div>
      )}

      {concepts.length > 0 && (
        <div>
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold mb-2">Concepts — pick topics to analyze</h3>
          <div className="flex flex-wrap gap-2">
            {concepts.map((c) => {
              const on = selected.includes(c)
              return (
                <button
                  key={c}
                  onClick={() => toggle(c)}
                  className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition ${
                    on ? 'bg-spark/15 border-spark/50 text-spark-600 dark:text-spark' : 'border-parchment-border text-ink-soft hover:border-gold dark:border-midnight-600 dark:text-mist/60'
                  }`}
                >
                  {on ? <X className="w-3 h-3" /> : <Plus className="w-3 h-3" />} {c}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {analyze.isError && (
        <div className="flex items-center gap-2 text-sm text-red-600"><AlertTriangle className="w-4 h-4" /> {analyze.error?.message}</div>
      )}

      {/* Results (fresh run or a loaded history entry) */}
      {results &&
        Object.entries(results).map(([topic, entry]) => (
          <TopicResult key={topic} topic={topic} entry={entry} onPickConcept={onPickConcept} onDiscover={onDiscover} />
        ))}

      {/* History */}
      {analyses.length > 0 && (
        <div className="rounded-xl border border-parchment-border bg-white/60 p-4 dark:border-midnight-600 dark:bg-midnight-800/50">
          <h3 className="font-display text-sm uppercase tracking-[0.14em] text-gold-deep dark:text-gold flex items-center gap-1.5 mb-2">
            <HistoryIcon className="w-3.5 h-3.5" /> Past analyses
          </h3>
          <ul className="space-y-1.5">
            {analyses.map((e) => (
              <li
                key={e.id}
                className={`group flex items-start gap-2 rounded-lg border p-2.5 cursor-pointer transition ${
                  viewingId === e.id ? 'border-gold bg-gold/5' : 'border-parchment-border hover:border-gold dark:border-midnight-600'
                }`}
                onClick={() => { setResults(e.results); setViewingId(e.id); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink dark:text-mist truncate">{(e.topics || []).join(', ') || '—'}</div>
                  <div className="text-xs text-ink-soft dark:text-mist/50">{fmtTime(e.timestamp)} · {(e.kinds || []).join(' + ')}</div>
                </div>
                <button
                  onClick={(ev) => { ev.stopPropagation(); delAnalysis.mutate({ project, id: e.id }); if (viewingId === e.id) { setResults(null); setViewingId(null) } }}
                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-ink-soft hover:text-red-600 dark:text-mist/50"
                  aria-label="Delete analysis"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
